
/**
 * Client-side pagination for a self-filtered card grid.
 *
 * The author page decides its own set — every article whose byline matches this
 * person — so Shopify's paginate tag cannot help: it slices blog.articles
 * BEFORE our filter runs, which would leave uneven pages and some showing none
 * of that author at all. Liquid also cannot read a page query parameter outside
 * that tag. So the cards are all rendered and paged here.
 *
 * Every post stays in the DOM, which is deliberate: an author page is an entity
 * hub, and keeping the full set crawlable is worth more than per-page URLs.
 */
(function () {
  var nav = document.querySelector('[data-msb-paginate]');
  if (!nav) return;
  var grid = document.querySelector('.msb-grid');
  if (!grid) return;

  var perPage = parseInt(nav.getAttribute('data-msb-paginate'), 10) || 9;
  var cards = Array.prototype.slice.call(grid.children);
  var pages = Math.ceil(cards.length / perPage);
  if (pages < 2) return;               // one page is not a pagination
  var current = 1;

  function button(label, page, cls) {
    var b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    if (cls) b.className = cls;
    if (page) b.addEventListener('click', function () { go(page); });
    return b;
  }

  /** 1 … 4 5 6 … 10 — first, last, and a window around the current page. */
  function numbers() {
    var out = [], i;
    for (i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || (i >= current - 1 && i <= current + 1)) out.push(i);
      else if (out[out.length - 1] !== '…') out.push('…');
    }
    return out;
  }

  function render() {
    nav.textContent = '';
    // Hidden, not disabled, at the ends — a greyed circle that cannot be
    // pressed is noise, and their own control shows only the arrow that works.
    if (current > 1) {
      var prev = button('←', current - 1, 'prev');
      prev.setAttribute('aria-label', 'Previous page');
      nav.appendChild(prev);
    }

    numbers().forEach(function (n) {
      if (n === '…') {
        var gap = document.createElement('span');
        gap.className = 'gap';
        gap.textContent = '…';
        gap.setAttribute('aria-hidden', 'true');
        nav.appendChild(gap);
        return;
      }
      var b = button(String(n), n);
      b.setAttribute('aria-label', 'Page ' + n);
      if (n === current) b.setAttribute('aria-current', 'page');
      nav.appendChild(b);
    });

    if (current < pages) {
      var next = button('→', current + 1, 'next');
      next.setAttribute('aria-label', 'Next page');
      nav.appendChild(next);
    }
  }

  function go(page) {
    current = page;
    cards.forEach(function (card, i) {
      var on = i >= (page - 1) * perPage && i < page * perPage;
      card.hidden = !on;
    });
    render();
    // Only reposition when the top of the grid is actually off-screen, and do
    // it instantly. Smooth-scrolling to the heading on every click fought the
    // host theme's sticky header and read as a jolt. scroll-margin-top on the
    // grid keeps it clear of that header without this script knowing its height.
    if (grid.getBoundingClientRect().top < 0) {
      grid.scrollIntoView({ block: 'start' });
    }
  }

  nav.setAttribute('aria-live', 'polite');
  go(1);
})();

/**
 * Sticky offset, measured rather than configured.
 *
 * Our sidebar has to clear the HOST theme's sticky chrome, and that height is
 * not knowable from our side. VIIA is the worked example: their theme publishes
 * --bs-navdrawer: 80px, but the real chrome is TWO stacked sticky elements —
 * a 35px topbar at top:0 and the 80px nav at top:35 — so the true bottom edge
 * is 115px. Three separate hardcoded guesses (80, 120, 112) each landed wrong
 * because they trusted one number for a two-part header.
 *
 * So: find every fixed/sticky element pinned near the top of the viewport,
 * take the lowest bottom edge, add the configured breathing room, and write it
 * back as the offset. Re-runs on resize, since header heights are responsive.
 * If nothing is found, the CSS token's own value stands.
 */
(function () {
  var scope = document.querySelector('.msb');
  if (!scope) return;

  function gap() {
    // The client's chosen breathing room, e.g. "2rem". Falls back to 32px.
    var v = getComputedStyle(scope).getPropertyValue('--msb-sticky-gap').trim();
    if (!v) return 32;
    var probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;height:' + v;
    scope.appendChild(probe);
    var px = probe.getBoundingClientRect().height;
    probe.remove();
    return px || 32;
  }

  function measure() {
    var lowest = 0;
    var nodes = document.body.querySelectorAll('header, [class*="header"], [class*="nav"], [class*="topbar"]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var cs = getComputedStyle(el);
      if (cs.position !== 'sticky' && cs.position !== 'fixed') continue;
      var r = el.getBoundingClientRect();
      // Only chrome actually pinned at the top, and not a full-height overlay
      // (cart drawers and menu panels are fixed too, and would swamp this).
      if (r.top > 120 || r.height < 20 || r.height > 240) continue;
      if (r.bottom > lowest) lowest = r.bottom;
    }
    if (lowest > 0) scope.style.setProperty('--msb-sticky-top', Math.round(lowest + gap()) + 'px');
  }

  measure();
  window.addEventListener('resize', measure);
  window.addEventListener('load', measure);
})();

(function () {
  var list = document.querySelector('.msb-toc ol');
  if (!list) return;

  // The Liquid template ships an empty list and lets us build it from the
  // rendered body's own headings, so the contents can never drift from the
  // article. The preview ships it pre-populated; either way we continue.
  if (!list.querySelector('a')) {
    // Headings live in the body wrapper. Today's plain body has no ids, so we
    // add them — the contents rail must work on the content that exists now,
    // not only on our own richer output.
    var found = document.querySelectorAll('.msb-body h2, .msb-article h2');
    found = Array.prototype.filter.call(found, function (h) {
      return h.textContent.trim().length > 0;
    });
    found.forEach(function (h, i) {
      if (!h.id) {
        h.id = 'section-' + (i + 1) + '-' + h.textContent.trim().toLowerCase()
          .replace(/[^a-z0-9]+/g, '-').slice(0, 40).replace(/-+$/, '');
      }
    });
    if (found.length < 2) {
      // Removed, not hidden. The grid gives a column to any shell that CONTAINS
      // a rail, so a display:none aside would still reserve 232px and leave the
      // article sitting left of the page's centre beside an empty gap.
      var aside = document.querySelector('.msb-toc');
      if (aside && aside.parentNode) {
        // The side column goes with it when the rail was all it held, for the same reason.
        var side = aside.closest('.msb-side');
        aside.parentNode.removeChild(aside);
        if (side && side.parentNode && !side.querySelector('.msb-sideproducts')) side.parentNode.removeChild(side);
      }
      return;
    }
    found.forEach(function (h) {
      var li = document.createElement('li');
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent;
      li.appendChild(a);
      list.appendChild(li);
    });
  }

  var links = list.querySelectorAll('a');
  if (!links.length) return;

  // Collapsible when a toggle is actually VISIBLE — style 2 shows one at every
  // width, style 4 only on narrow screens where the contents rail moves above
  // the article. offsetParent is null when CSS has hidden it, so one test
  // covers both without the script knowing which template it is in.
  //
  // Re-read on every resize, not once at load. Decided once, a page opened
  // wide and then narrowed — a tablet rotated to portrait, a desktop window
  // dragged thin — showed the toggle with no handler behind it: the list sat
  // open under a "+" that did nothing. The handler is always wired; whether
  // the list is folded follows the toggle's visibility as it changes.
  var toggle = document.querySelector('.msb-toc .toggle');
  var inline = false;
  function syncCollapse() {
    inline = !!toggle && toggle.offsetParent !== null;
    // Folded only while the toggle is showing, and then only until the reader
    // opens it. Back at sidebar width the list is always visible, whatever
    // state the toggle was left in.
    list.hidden = inline && toggle.getAttribute('aria-expanded') !== 'true';
  }
  if (toggle) {
    toggle.addEventListener('click', function () {
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!open));
      list.hidden = open;
    });
  }
  syncCollapse();
  // Registered before the scrollspy's own resize listener, so on a resize the
  // collapse state is current by the time the spy decides whether to run.
  window.addEventListener('resize', syncCollapse, { passive: true });

  // The scrollspy is always installed and simply stands down while the list is
  // collapsed: a collapsed rail has no visible marker to move, and the spy
  // would fight the toggle for control of the list. Installing it
  // unconditionally is what lets a page opened narrow start tracking once it
  // is widened into a sidebar.
  (function () {
    function setActive(id) {
      var target = null;
      links.forEach(function (a) {
        var on = a.getAttribute('href') === '#' + id;
        a.classList.toggle('active', on);
        if (on) target = a;
      });
      // Move one marker rather than restyling the active link: changing
      // font-weight or adding a border would reflow the rail on every scroll.
      // The marker is the ol's ::before, reached through custom properties —
      // a pseudo-element cannot be a DOM child of <ol> and a real element may
      // not be one either.
      if (target) {
        // The marker is full-height and scaled down, rather than sized in
        // pixels: animating scale is composited, animating height repaints on
        // every scroll tick. The scroll handler runs constantly, so this is
        // the one place in the template where that distinction is worth the
        // extra line.
        list.style.setProperty('--toc-marker-y', target.offsetTop + 'px');
        var full = list.offsetHeight || 1;
        list.style.setProperty('--toc-marker-scale', (target.offsetHeight / full).toString());
      }
    }

    var heads = [];
    links.forEach(function (a) {
      var el = document.getElementById(a.getAttribute('href').slice(1));
      if (el) heads.push(el);
    });
    if (!heads.length) return;

    // Resolve the active heading from where the headings ARE, not from which
    // one last crossed a boundary. An observer that only reacts when a heading
    // enters the band never fires as one leaves upward, so scrolling back up
    // left the rail pointing at whatever entered most recently rather than at
    // the section actually on screen.
    var line = function () { return window.innerHeight * 0.3; };
    function update() {
      if (inline) return;
      var edge = line();
      var current = heads[0].id;
      for (var i = 0; i < heads.length; i++) {
        if (heads[i].getBoundingClientRect().top <= edge) current = heads[i].id;
      }
      setActive(current);
    }

    // rAF-gated: scroll fires far faster than paint, and update() reads layout.
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { update(); ticking = false; });
    }, { passive: true });
    // Link heights change with the rail's width, so the marker's scale has to
    // be recomputed when the column reflows.
    window.addEventListener('resize', update, { passive: true });
    update();
  })();
})();

(function () {
  // The stored body ships tables unwrapped: content-engine emits a bare
  // <table> with inline styles, not the renderer's .msb-table. A table cannot
  // shrink below its min-content, and a five-column comparison is wider than
  // a phone, so with no box of its own to scroll in it widened the whole
  // article column. Give it the scrolling box the renderer would have.
  var tables = document.querySelectorAll('.msb-body table');
  Array.prototype.forEach.call(tables, function (t) {
    if (t.closest('.msb-table, .msb-scroll')) return; // already has one
    var box = document.createElement('div');
    box.className = 'msb-scroll';
    t.parentNode.insertBefore(box, t);
    box.appendChild(t);
  });
})();

(function () {
  var buttons = document.querySelectorAll('[data-msb-copy]');
  buttons.forEach(function (b) {
    b.addEventListener('click', function () {
      // Absolute, always. The attribute carries a Shopify path like
      // /blogs/education/post — pasting that into Slack or a browser goes
      // nowhere, and the same relative value is what the social share links
      // were handing to LinkedIn and X.
      var raw = b.getAttribute('data-msb-copy') || location.href;
      var value = raw.indexOf('http') === 0 ? raw : location.origin + raw;

      function confirmCopied() {
        b.classList.add('copied');
        var had = b.getAttribute('aria-label');
        b.setAttribute('aria-label', 'Link copied');
        setTimeout(function () {
          b.classList.remove('copied');
          if (had) b.setAttribute('aria-label', had);
        }, 1800);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value).then(confirmCopied).catch(function () {});
      } else {
        // Older Safari and any non-secure context have no clipboard API.
        var f = document.createElement('textarea');
        f.value = value;
        f.setAttribute('readonly', '');
        f.style.cssText = 'position:absolute;left:-9999px';
        document.body.appendChild(f);
        f.select();
        try { document.execCommand('copy'); confirmCopied(); } catch (e) {}
        f.remove();
      }
    });
  });
})();

(function () {
  // Live article search over Shopify's predictive-search endpoint
  // (/search/suggest.json) — present on every store, no setup. Results render
  // in a dropdown under the field; Enter still submits to the full search
  // page, so the control works even with this script absent.
  var form = document.querySelector('.msb-search');
  if (!form) return;
  var input = form.querySelector('input[type="search"]');
  if (!input) return;

  var box = document.createElement('div');
  box.className = 'results';
  box.setAttribute('role', 'listbox');
  box.hidden = true;
  form.appendChild(box);

  function close() { box.hidden = true; box.innerHTML = ''; }

  function render(articles) {
    box.innerHTML = '';
    if (!articles.length) {
      var none = document.createElement('p');
      none.className = 'none';
      none.textContent = 'No matching posts';
      box.appendChild(none);
    } else {
      articles.slice(0, 5).forEach(function (a) {
        var link = document.createElement('a');
        link.href = a.url;
        link.setAttribute('role', 'option');
        if (a.image) {
          var img = document.createElement('img');
          img.src = a.image;
          img.alt = '';
          img.loading = 'lazy';
          link.appendChild(img);
        }
        var label = document.createElement('span');
        label.textContent = a.title;
        link.appendChild(label);
        box.appendChild(link);
      });
    }
    box.hidden = false;
  }

  var timer = null;
  var seq = 0;
  input.addEventListener('input', function () {
    var q = input.value.trim();
    if (timer) clearTimeout(timer);
    if (q.length < 2) { close(); return; }
    timer = setTimeout(function () {
      var mine = ++seq;
      var base = (form.getAttribute('action') || '/search').replace(/\/$/, '');
      var url = base + '/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=article&resources[limit]=5';
      fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (mine !== seq) return; // a newer keystroke owns the box now
          var results = (((data || {}).resources || {}).results || {});
          render(results.articles || []);
        })
        .catch(function () { close(); });
    }, 200);
  });

  input.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { close(); input.blur(); }
  });
  document.addEventListener('click', function (e) {
    if (!form.contains(e.target)) close();
  });
})();

/**
 * Promote the pipeline's "Key takeaways" block into the boxed component.
 *
 * The TypeScript renderer (src/render.ts, renderTakeaways) emits
 * <aside class="msb-takeaways"> for this, but that path only runs in the local
 * preview. On the store the body is content-engine HTML injected through
 * {{ article.content }}, which arrives as a plain label paragraph followed by a
 * plain list, so the styled component never appeared on any client site.
 *
 * The shape is stable: content-engine's writer emits KEY TAKEAWAYS as a
 * validator-enforced skimmer block right after the opening answer. The heading
 * pattern is kept in step with TAKEAWAYS_HEADING in src/render.ts.
 *
 * Scoped to .msb-body only. .msb-prose is the preview's render path and already
 * has the real component; running there would double-wrap it.
 */
(function () {
  var HEADING = /\b(key takeaways?|the short version|in short|tl;?dr)\b/i;

  var body = document.querySelector('.msb-body');
  if (!body) return;
  if (body.querySelector('.msb-takeaways')) return;

  var paras = body.querySelectorAll(':scope > p');
  for (var i = 0; i < paras.length; i++) {
    var p = paras[i];
    var label = p.textContent || '';
    if (!HEADING.test(label)) continue;

    // A label paragraph, not a sentence that happens to mention the phrase:
    // short, and its whole content is the emphasis element.
    var strong = p.querySelector('strong, b');
    if (!strong) continue;
    if (strong.textContent.trim() !== label.trim()) continue;
    if (label.trim().length > 40) continue;

    var list = p.nextElementSibling;
    if (!list) continue;
    if (list.tagName !== 'UL' && list.tagName !== 'OL') continue;

    var aside = document.createElement('aside');
    aside.className = 'msb-takeaways';
    var h2 = document.createElement('h2');
    h2.textContent = label.trim().replace(/:$/, '');

    p.parentNode.insertBefore(aside, p);
    aside.appendChild(h2);
    aside.appendChild(list);
    p.parentNode.removeChild(p);
    return;
  }
})();

// Sidebar products (features.relatedProducts) stick directly under the contents rail, which needs
// the rail's height: CSS cannot know it, since it depends on how many sections the article has and
// whether their titles wrap. Runs last, after the rail has been built or removed. With no rail the
// height is 0 and the products stick at the normal offset.
(function () {
  var side = document.querySelector('.msb-sideproducts');
  if (!side) return;
  var scope = document.querySelector('.msb') || document.documentElement;
  var rail = document.querySelector('.msb-toc');
  function set() {
    var live = rail && rail.parentNode ? rail : null;
    var h = live ? Math.round(live.getBoundingClientRect().height) : 0;
    scope.style.setProperty('--msb-toc-h', h + 'px');
  }
  set();
  if (rail && window.ResizeObserver) new ResizeObserver(set).observe(rail);
  window.addEventListener('load', set);
  window.addEventListener('resize', set);
})();

/**
 * Click-through on a review copy.
 *
 * Before go-live nothing on the store is assigned to our templates, so a
 * reviewer reaches them with ?view=<suffix> — and loses it on the first click,
 * because a link inside the page carries no view. This keeps the view on the
 * links inside our own markup so blog -> article -> author can be walked the
 * way a visitor will walk it.
 *
 * Inert on the live theme by construction: Shopify.theme.role is 'main' there,
 * and after go-live the templates are assigned so no view is needed.
 */
(function () {
  var theme = window.Shopify && window.Shopify.theme;
  if (!theme || theme.role === 'main') return;
  var root = document.querySelector('.msb');
  if (!root) return;
  root.addEventListener('click', function (event) {
    var link = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!link || link.target === '_blank') return;
    var url;
    try { url = new URL(link.getAttribute('href'), location.href); } catch (e) { return; }
    if (url.origin !== location.origin || url.searchParams.has('view')) return;
    var view = /^\/blogs\//.test(url.pathname) ? 'mentionstack'
      : /^\/pages\//.test(url.pathname) && link.closest('.msb-byline, .msb-author, .msb-related, .msb-card') ? 'mentionstack-author'
      : '';
    if (!view) return;
    url.searchParams.set('view', view);
    link.setAttribute('href', url.pathname + url.search + url.hash);
  }, true);
})();
